/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Box } from '@landtrustinc/design-system';
import { bool, oneOfType, func } from 'prop-types';
import classNames from 'classnames';
import { HashLink as Link } from 'react-router-hash-link';
import { get } from 'lodash';
import { scrollWithOffset } from '../../util/listing';
import { toggleFavoritePackages } from '../../ducks/user.duck';

import { propTypes, LISTING_STATE_CLOSED } from '../../util/types';
import { HUNT_ACTIVITY, OUTDOOR_RECREATION_ACTIVITY } from '../../marketplace-custom-config';
import { createSlug } from '../../util/urlHelpers';

import { PrimaryButton, HorizontalPackageCard, Tabs, Tab, NamedLink } from '..';

import css from './SectionAvailablePackages.module.css';

const TABS = {
  ALL: 'all',
  HUNT: 'hunt',
  OUTDOOR_RECREATION: 'outdoor_recreation',
};

const SectionAvailablePackages = props => {
  const [activeTab, setActiveTab] = useState(TABS.ALL);

  const {
    isOwnListing,
    listing,
    listingPage,
    rootClassName,
    followSubjectMutation,
    currentUser,
    onToggleFavoritePackage,
  } = props;

  const packages = get(listing, 'attributes.publicData.packages', []);
  const activities = get(listing, 'attributes.publicData.categories', {});
  const listingTitle = listing?.attributes?.title || '';
  const slug = createSlug(listingTitle);
  const id = listing.id.uuid;

  const publishedPackages = packages.filter(
    item => item.isPublished && activities.includes(item.activity)
  );

  const huntPackages = publishedPackages.filter(item => item.activity === HUNT_ACTIVITY);
  const outdoorRecreationPackages = publishedPackages.filter(
    item => item.activity === OUTDOOR_RECREATION_ACTIVITY
  );

  const activitesPackages = [];

  if (
    huntPackages.length &&
    [TABS.ALL, TABS.HUNT].includes(activeTab) &&
    activities.includes(HUNT_ACTIVITY)
  ) {
    activitesPackages.push({
      title: <FormattedMessage id="SectionAvailablePackages.huntTitle" />,
      packages: huntPackages,
      tab: TABS.HUNT,
    });
  }

  if (
    outdoorRecreationPackages.length &&
    [TABS.ALL, TABS.OUTDOOR_RECREATION].includes(activeTab) &&
    activities.includes(OUTDOOR_RECREATION_ACTIVITY)
  ) {
    activitesPackages.push({
      title: <FormattedMessage id="SectionAvailablePackages.outdoorRecreationTitle" />,
      packages: outdoorRecreationPackages,
      tab: TABS.OUTDOOR_RECREATION,
    });
  }

  const hasListingState = !!listing.attributes.state;
  const isClosed = hasListingState && listing.attributes.state === LISTING_STATE_CLOSED;
  const showPackagesButton = hasListingState && !isClosed;

  const classes = classNames(rootClassName || css.root);

  // Handle favorite toggle - wraps the Redux action with auto-follow logic
  const handleToggleFavorite = (pkg, followMutation) => {
    if (!currentUser) return;

    const favoritePackages = currentUser?.attributes?.profile?.publicData?.favoritePackages || [];
    const isFavorited = favoritePackages.indexOf(`${listing.id.uuid}:${pkg.id}`) > -1;

    // Toggle favorite
    onToggleFavoritePackage(pkg, listing);

    // Auto-follow property when favoriting (not when unfavoriting)
    if (!isFavorited && followMutation) {
      followMutation(currentUser.id.uuid, listing.id.uuid, { sms: true, email: true });
    }
  };

  return (
    <div className={classes} id="packagesMobile">
      <h1 className={css.title}>
        <FormattedMessage id="SectionAvailablePackages.title" />
      </h1>

      {activitesPackages.length > 0 && (
        <div className={css.tabWrapper}>
          <Tabs>
            <Tab handleClick={() => setActiveTab(TABS.ALL)} isSelected={activeTab === TABS.ALL}>
              <FormattedMessage
                id="SectionAvailablePackages.tabAll"
                values={{ count: publishedPackages.length }}
              />
            </Tab>

            {huntPackages.length > 0 && activities.includes(HUNT_ACTIVITY) && (
              <Tab handleClick={() => setActiveTab(TABS.HUNT)} isSelected={activeTab === TABS.HUNT}>
                <FormattedMessage
                  id="SectionAvailablePackages.tabHunting"
                  values={{ count: huntPackages.length }}
                />
              </Tab>
            )}

            {outdoorRecreationPackages.length > 0 &&
              activities.includes(OUTDOOR_RECREATION_ACTIVITY) && (
                <Tab
                  handleClick={() => setActiveTab(TABS.OUTDOOR_RECREATION)}
                  isSelected={activeTab === TABS.OUTDOOR_RECREATION}
                >
                  <FormattedMessage
                    id="SectionAvailablePackages.tabOutdoorRecreation"
                    values={{ count: outdoorRecreationPackages.length }}
                  />
                </Tab>
              )}
          </Tabs>
        </div>
      )}

      {activitesPackages.map((activity, index) => (
        <Box key={`activity-section-${index}`} mb="var(--spacing-6)">
          <div className={css.activityTitle}>{activity.title}</div>

          <Box display="flex" flexDirection="column" gap="var(--spacing-6)">
            {activity.packages.map(item => (
              <NamedLink
                key={item.id}
                className={css.linkRoot}
                name="PackagePage"
                params={{ id, slug, packageId: item.id }}
              >
                <HorizontalPackageCard
                  listing={listing}
                  packageId={item.id}
                  item={item}
                  currentUser={currentUser}
                  followSubjectMutation={followSubjectMutation}
                  onToggleFavoritePackage={currentUser ? handleToggleFavorite : null}
                  onClick={() => {}}
                />
              </NamedLink>
            ))}
          </Box>
        </Box>
      ))}
      {listingPage && (
        <div className={css.openBookingForm}>
          {showPackagesButton ? (
            <Link
              className={css.availLink}
              onClick={e => e.preventDefault()}
              to="#packagesMobile"
              scroll={scrollWithOffset}
            >
              <PrimaryButton
                className={css.bookButton}
                isFullWidth
                onClick={e => e.preventDefault()}
              >
                <FormattedMessage id="SectionAvailablePackages.ctaButtonMessage" />
              </PrimaryButton>
            </Link>
          ) : isOwnListing ? null : !isClosed ? (
            <div className={css.closedListingButton}>
              <FormattedMessage id="SectionAvailablePackages.loadingText" />
            </div>
          ) : (
            <div className={css.closedListingButton}>
              <FormattedMessage id="SectionAvailablePackages.closedListingButtonText" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

SectionAvailablePackages.defaultProps = {
  isOwnListing: false,
  currentUser: null,
  followSubjectMutation: null,
};

SectionAvailablePackages.propTypes = {
  listing: oneOfType([propTypes.listing, propTypes.ownListing]).isRequired,
  isOwnListing: bool,
  currentUser: propTypes.currentUser,
  onToggleFavoritePackage: func.isRequired,
  followSubjectMutation: func,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  return { currentUser };
};

const mapDispatchToProps = dispatch => ({
  onToggleFavoritePackage: (pkg, listing) => {
    dispatch(toggleFavoritePackages(pkg, listing));
  },
});

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withRouter,
  injectIntl
)(SectionAvailablePackages);
